import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { getImportBatchDetails, ImportBatchDetails } from '../../services/importService';

interface BatchDetailModalProps {
  open: boolean;
  batchId: number | null;
  onClose: () => void;
}

const BatchDetailModal: React.FC<BatchDetailModalProps> = ({ open, batchId, onClose }) => {
  const [details, setDetails] = useState<ImportBatchDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !batchId) return;
    setLoading(true);
    setError(null);
    getImportBatchDetails(batchId)
      .then(setDetails)
      .catch((err) => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [open, batchId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <span className="text-base font-semibold text-gray-800">Batch #{batchId} Details</span>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        )}
        {error && (
          <div className="text-red-500 text-sm py-4">{error}</div>
        )}
        {!loading && details && (
          <div className="space-y-4 text-sm">
            {/* Header info */}
            <div className="grid grid-cols-2 gap-4 text-gray-600">
              <div><span className="text-gray-400">Type:</span> <span className="font-medium capitalize">{details.type}</span></div>
              <div><span className="text-gray-400">Status:</span> <span className={`font-medium ${
                details.review_status === 'approved' ? 'text-green-600' :
                details.review_status === 'rejected' ? 'text-red-500' : 'text-yellow-600'
              }`}>{details.review_status}</span></div>
              <div><span className="text-gray-400">File:</span> {details.original_filename || '—'}</div>
              <div><span className="text-gray-400">Created:</span> {new Date(details.created_at).toLocaleString()}</div>
            </div>

            {/* Supply summary */}
            {details.supplySummary.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Supply Positions</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="pb-2 pr-4">Formula</th>
                      <th className="pb-2 pr-4">Type</th>
                      <th className="pb-2 pr-4 text-right">Count</th>
                      <th className="pb-2 text-right">Quantity (MT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {details.supplySummary.map((r, idx) => (
                      <tr key={idx} className="text-gray-700">
                        <td className="py-1.5 pr-4 font-medium">{r.formula_code}</td>
                        <td className="py-1.5 pr-4 text-gray-500">{r.supply_type.replace(/_/g, ' ')}</td>
                        <td className="py-1.5 pr-4 text-right font-mono">{r.count}</td>
                        <td className="py-1.5 text-right font-mono">{parseFloat(r.total_remaining || r.total_quantity || '0').toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Demand summary */}
            {details.demandSummary && details.demandSummary.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Demand Positions</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="pb-2 pr-4">Formula</th>
                      <th className="pb-2 pr-4">Kind</th>
                      <th className="pb-2 pr-4 text-right">Count</th>
                      <th className="pb-2 text-right">Quantity (MT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {details.demandSummary.map((r, idx) => (
                      <tr key={idx} className="text-gray-700">
                        <td className="py-1.5 pr-4 font-medium">{r.formula_code}</td>
                        <td className="py-1.5 pr-4 text-gray-500">{r.demand_kind.replace(/_/g, ' ')}</td>
                        <td className="py-1.5 pr-4 text-right font-mono">{r.count}</td>
                        <td className="py-1.5 text-right font-mono">{parseFloat(r.total_quantity || '0').toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Hedge summary */}
            {details.hedgeSummary && details.hedgeSummary.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Hedge Positions</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="pb-2 pr-4">Formula</th>
                      <th className="pb-2 pr-4">Side</th>
                      <th className="pb-2 pr-4 text-right">Count</th>
                      <th className="pb-2 text-right">Quantity (MT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {details.hedgeSummary.map((r, idx) => (
                      <tr key={idx} className="text-gray-700">
                        <td className="py-1.5 pr-4 font-medium">{r.formula_code}</td>
                        <td className="py-1.5 pr-4 text-gray-500">{r.position_side}</td>
                        <td className="py-1.5 pr-4 text-right font-mono">{r.count}</td>
                        <td className="py-1.5 text-right font-mono">{parseFloat(r.total_quantity || '0').toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Error summary */}
            {details.error_summary && (
              <div className="text-red-500 bg-red-50 px-3 py-2 rounded text-xs">
                {details.error_summary}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchDetailModal;
